import {
  EOB_CLASSIFICATIONS,
  getCategoryMetadata,
  isAppealDeniedUpheldAutomated,
  isAppealDeniedUpheldOutOfNetwork,
  isClaimAcknowledged,
  isClaimAppealAcknowledgement,
  isClaimAppealDeniedUpheldMedicalNecessity,
  isClaimAppealDeniedUpheldNonCovered,
  isClaimAppealDeniedUpheldNoPriorAuthorization,
  isClaimAppealOverturned,
  isClaimAppealRejectedTimely,
  isClaimDeniedExperimental,
  isClaimRequestMedicalRecords,
  isNonFinancialEob,
  isPaAutomatedCategory,
  type Subcategory,
} from '~/entities/categories'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { getIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { CLAIM_PHASES, DOCUMENT_SERVER_STATUS } from '../config'
import type { UIDocumentStep } from './types'

const STEPS_ORDER_WITH_TAGGING: UIDocumentStep[] = [
  'clustering',
  'classification',
  'data',
  'carc_rarc',
  'patient',
  'finished',
]

const STEPS_ORDER: UIDocumentStep[] = [
  'clustering',
  'classification',
  'data',
  'patient',
  'finished',
]

export const EOB_STEPS_ORDER: UIDocumentStep[] = [
  'clustering',
  'classification',
  'eob_payment_deposit',
  'eob_duplication',
  'eob_processing',
  'finished',
]

export const EOB_NON_FINANCIAL_STEPS_ORDER: UIDocumentStep[] = [
  'clustering',
  'classification',
  'eob_duplication',
  'eob_processing',
  'finished',
]

function isEobClassification(category: string | null | undefined): boolean {
  return EOB_CLASSIFICATIONS.includes(category as (typeof EOB_CLASSIFICATIONS)[number])
}

export function isTaggingClassification(
  category: string | null | undefined,
  subcategories: Subcategory[],
): boolean {
  const phase = getCategoryMetadata({ category, subcategories })?.phase.label
  return phase != null && CLAIM_PHASES.includes(phase)
}

export interface StepOrderFlags {
  isTaggingEnabled: boolean
  isEob: boolean
  isNonFinancialEob: boolean
}

function getStepsOrder({
  isTaggingEnabled,
  isEob,
  isNonFinancialEob: isNonFinancial,
}: StepOrderFlags): UIDocumentStep[] {
  if (isNonFinancial) return EOB_NON_FINANCIAL_STEPS_ORDER
  if (isEob) return EOB_STEPS_ORDER
  if (isTaggingEnabled) return STEPS_ORDER_WITH_TAGGING
  return STEPS_ORDER
}

/**
 * Last real step of the flow = element immediately before the `finished`
 * sentinel in the step-order array chosen by {@link getStepsOrder}.
 *
 * Used to trigger side-effects (e.g. PDF tab close reminder) after the final
 * validation in each flow (standard, tagging-enabled, EOB financial, EOB
 * non-financial).
 */
export function isLastStep(step: UIDocumentStep, flags: StepOrderFlags): boolean {
  const order = getStepsOrder(flags)
  const finishedIndex = order.indexOf('finished')
  if (finishedIndex <= 0) return false
  return order[finishedIndex - 1] === step
}

export function getDocumentStep(
  document: apiClient.Document,
  subcategories: Subcategory[],
): UIDocumentStep {
  if (
    document.review_status === DOCUMENT_SERVER_STATUS.reviewed ||
    document.review_status === DOCUMENT_SERVER_STATUS.auto_reviewed
  ) {
    return 'finished'
  }
  // `appeal_acknowledged` / `claim_acknowledged` and the two automated
  // denied-upheld appeal outcomes need neither tag nor data validation — the
  // backend hardcodes their tags (timeframe tags for the acknowledged pair, the
  // reason's CARC/RARC set for the denied-upheld pair) and auto-reviews them
  // outright when patient matching was deterministic. The ones that reach a
  // reviewer are exactly the ones whose match needs a human, so matching is
  // their entry point.
  //
  // This branch runs before the `carc_rarc` overrides below on purpose:
  // `appeal_denied_upheld_no_prior_authorization` also satisfies the metadata
  // check behind `2026-07-claim-appeal-denied-upheld-npa-tagging-entry`, and
  // Matching now supersedes that older Tagging entry point for it.
  if (
    isClaimAppealAcknowledgement(document.category) ||
    isClaimAcknowledged(document.category) ||
    isAppealDeniedUpheldAutomated(document.category)
  ) {
    return 'patient'
  }
  if (
    getIsFeatureEnabled('2026-09-pa-matching-entry') &&
    isPaAutomatedCategory(document.category)
  ) {
    return 'patient'
  }
  if (
    getIsFeatureEnabled('2026-10-appeal-denied-upheld-oon-matching-entry') &&
    isAppealDeniedUpheldOutOfNetwork(document.category)
  ) {
    return 'patient'
  }
  if (
    getIsFeatureEnabled('2026-07-claim-appeal-denied-upheld-mn-tagging-entry') &&
    isClaimAppealDeniedUpheldMedicalNecessity(
      getCategoryMetadata({ category: document.category, subcategories }),
    )
  ) {
    return 'carc_rarc'
  }
  if (
    getIsFeatureEnabled('2026-07-claim-appeal-denied-upheld-npa-tagging-entry') &&
    isClaimAppealDeniedUpheldNoPriorAuthorization(
      getCategoryMetadata({ category: document.category, subcategories }),
    )
  ) {
    return 'carc_rarc'
  }
  if (
    getIsFeatureEnabled('2026-08-claim-appeal-overturned-tagging-entry') &&
    isClaimAppealOverturned(getCategoryMetadata({ category: document.category, subcategories }))
  ) {
    return 'carc_rarc'
  }
  if (
    getIsFeatureEnabled('2026-08-claim-request-medical-records-tagging-entry') &&
    isClaimRequestMedicalRecords(
      getCategoryMetadata({ category: document.category, subcategories }),
    )
  ) {
    return 'carc_rarc'
  }
  if (
    getIsFeatureEnabled('2026-08-claim-appeal-denied-upheld-nc-tagging-entry') &&
    isClaimAppealDeniedUpheldNonCovered(
      getCategoryMetadata({ category: document.category, subcategories }),
    )
  ) {
    return 'carc_rarc'
  }
  if (
    getIsFeatureEnabled('2026-08-claim-appeal-rejected-timely-tagging-entry') &&
    isClaimAppealRejectedTimely(getCategoryMetadata({ category: document.category, subcategories }))
  ) {
    return 'carc_rarc'
  }
  if (
    getIsFeatureEnabled('2026-10-claim-denied-experimental-tagging-entry') &&
    isClaimDeniedExperimental(getCategoryMetadata({ category: document.category, subcategories }))
  ) {
    return 'carc_rarc'
  }
  if (!document.reviewed_step) {
    return 'classification'
  }
  // 'carc_rarc' is the same as 'data'
  if (document.reviewed_step === 'carc_rarc') {
    return 'patient'
  }

  const isEob = getIsFeatureEnabled('2026-02-eob-phase-1') && isEobClassification(document.category)
  const isNonFinancial = isEob && isNonFinancialEob(document.category)
  const isTaggingEnabled = isTaggingClassification(document.category, subcategories)
  const stepsOrder = getStepsOrder({ isTaggingEnabled, isEob, isNonFinancialEob: isNonFinancial })

  const prevStepIndex = stepsOrder.indexOf(document.reviewed_step)
  if (prevStepIndex === -1) {
    return 'classification'
  }
  return stepsOrder[prevStepIndex + 1]
}
